/**
 * Zukunftsklima (Ausbaustufe B, docs/methodik.md Abschnitt 10).
 * Änderungen von Kennzahlen aus Klimamodell-Tageswerten, je Quelle.
 */

export type FutureIndicator = 'hdd' | 'cdd' | 'hotDays' | 'veryHotDays' | 'tropicalNights' | 'heatEvents' | 'heatDays' | 'txx' | 'tMean';

export type FutureSource = 'ch2025' | 'cckp';

export interface Spread {
  p10: number;
  p50: number;
  p90: number;
  /** Anzahl Modelle bzw. Modellketten */
  n: number;
}

export type Robustness = 'robust' | 'keine Änderung' | 'widersprüchlich' | 'keine Daten';

export interface FutureLevel {
  key: string;
  label: string;
  /** Globale Erwärmung gegenüber 1850–1900 in °C (bei Welt: Näherung nach IPCC AR6). */
  gwl: number;
  gwlApprox: boolean;
}

export interface FutureRow {
  indicator: FutureIndicator;
  /** Definition der Quelle, z. B. «Tagesmaximum ≥ 30 °C». */
  definition: string;
  unit: string;
  /** Referenzwert der Quelle (Median). */
  sourceRef: number | null;
  deltas: Record<string, Spread | null>;
  robustness?: Record<string, Robustness | null>;
}

export interface FutureFileRecord {
  url: string;
  bytes: number;
  etag: string | null;
  sha256?: string;
  retrievedAt: string;
}

export interface FutureResult {
  source: FutureSource;
  sourceLabel: string;
  licence: string;
  referencePeriod: string;
  levels: FutureLevel[];
  rows: FutureRow[];
  location: {
    kind: 'station' | 'cell';
    id: string;
    name: string;
    lat: number;
    lon: number;
    elevation?: number;
    distanceKm: number;
    elevationDiffM?: number;
    representative: boolean;
  };
  notes: string[];
  files: FutureFileRecord[];
  missingShare?: number;
  computedAt: string;
  thresholdsKey: string;
}

export interface FutureThresholds {
  hddBase: number;
  cddBase: number;
  hotDayThreshold: number;
  heatPeriodMinDays: number;
}

export function thresholdsKey(t: FutureThresholds): string {
  return `${t.hddBase}|${t.cddBase}|${t.hotDayThreshold}|${t.heatPeriodMinDays}`;
}

/** Perzentil nach linearer Interpolation (Typ 7, wie R/NumPy-Standard). */
export function percentile(sorted: number[], q: number): number {
  if (!sorted.length) return NaN;
  const h = (sorted.length - 1) * q;
  const lo = Math.floor(h);
  const hi = Math.ceil(h);
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (h - lo);
}

export function spreadOf(values: number[]): Spread | null {
  const v = values.filter((x) => Number.isFinite(x)).sort((a, b) => a - b);
  if (!v.length) return null;
  return { p10: percentile(v, 0.1), p50: percentile(v, 0.5), p90: percentile(v, 0.9), n: v.length };
}

export function distanceKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const r = Math.PI / 180;
  const a = Math.sin(((lat2 - lat1) * r) / 2) ** 2 + Math.cos(lat1 * r) * Math.cos(lat2 * r) * Math.sin(((lon2 - lon1) * r) / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.sqrt(a));
}

/** Richtwert am Gebäude: Gebäudereferenz + Änderung, bei Zähl- und Gradtagen begrenzt. */
export function guidanceValue(indicator: FutureIndicator, buildingRef: number, delta: number): number {
  const v = buildingRef + delta;
  switch (indicator) {
    case 'hotDays':
    case 'veryHotDays':
    case 'tropicalNights':
    case 'heatDays':
      return Math.min(365, Math.max(0, v));
    case 'heatEvents':
    case 'hdd':
    case 'cdd':
      return Math.max(0, v);
    default:
      return v;
  }
}
